/**
 * Leviathan Web - Access levels
 * Quasar Prime LLC
 *
 * The `UserRole` enum, mirrored from apps/api/prisma/schema.prisma.
 *
 * THIS is the field the API enforces: `requireAdmin` reads it off the JWT,
 * which is populated from this column at login. It is distinct from the
 * separate `Role` model (a named permission group, surfaced as `roleGroup`),
 * which nothing enforces today. Presenting them as one thing is what let an
 * admin change a "role" in the UI, receive a 200, and change nothing about what
 * that person could actually do.
 */

export interface RoleOption {
  value: string;
  label: string;
  description: string;
  /** Grants administrator powers — user management, settings, invitations. */
  isAdmin: boolean;
}

export const ROLE_OPTIONS: RoleOption[] = [
  {
    value: 'OWNER',
    label: 'Owner',
    description: 'Full access, including other owners. Only an owner can grant this.',
    isAdmin: true,
  },
  {
    value: 'ADMIN',
    label: 'Administrator',
    description: 'Manages users, invitations, and company settings.',
    isAdmin: true,
  },
  {
    value: 'MANAGER',
    label: 'Manager',
    description: 'Full operational access. Cannot manage users or settings.',
    isAdmin: false,
  },
  {
    value: 'USER',
    label: 'User',
    description: 'Day-to-day work: orders, dispatch, shipments, invoices.',
    isAdmin: false,
  },
  {
    value: 'READONLY',
    label: 'Read Only',
    description: 'Can view but not change anything.',
    isAdmin: false,
  },
];

/**
 * Roles the schema still accepts but which are not offered when assigning one.
 * An account already holding one keeps it — it is shown rather than silently
 * rewritten — but nothing new is put into these.
 */
const LEGACY_ROLES = ['SUPER_ADMIN', 'DISPATCHER', 'DRIVER', 'ACCOUNTANT', 'SALES', 'VIEWER'];

/** Admin roles, mirroring ADMIN_ROLES in the API's authenticate middleware. */
export const ADMIN_ROLES = ['OWNER', 'SUPER_ADMIN', 'ADMIN'];

export const isAdminRole = (role: string | undefined | null): boolean =>
  !!role && ADMIN_ROLES.includes(role);

export const roleLabel = (role: string | null | undefined): string => {
  if (!role) return 'None';
  const known = ROLE_OPTIONS.find((r) => r.value === role);
  if (known) return known.label;
  if (LEGACY_ROLES.includes(role)) return `${role} (legacy)`;
  return role;
};

/**
 * Options to show in a picker for a user who currently holds `current`.
 * Includes their existing role even when it is legacy, so opening the form does
 * not silently propose changing it.
 */
export const roleOptionsFor = (current?: string | null): RoleOption[] => {
  if (current && !ROLE_OPTIONS.some((r) => r.value === current)) {
    return [
      ...ROLE_OPTIONS,
      {
        value: current,
        label: roleLabel(current),
        description: 'Existing role, kept as-is. Not offered for new assignments.',
        isAdmin: ADMIN_ROLES.includes(current),
      },
    ];
  }
  return ROLE_OPTIONS;
};
